import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import styled from "styled-components";

const Nav = styled.nav`
  padding: 6px 16px;
  font-size: 0.9em;
  background: #f8f8f8;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  box-sizing: border-box;
  /* Above bottom sheet (500), map-overlays-container (510), ad-hoc pill (520). */
  position: relative;
  z-index: 600;
  flex-shrink: 0;
`;

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileBack = styled.div`
  display: none;
  flex: 1;
  min-width: 0;
  align-items: center;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const RightSlot = styled.div`
  flex-shrink: 0;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  white-space: nowrap;
  min-width: 0;

  &:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &:not(:last-child)::after {
    content: "›";
    margin: 0 8px;
    color: #999;
    flex-shrink: 0;
  }
`;

const Crumb = styled(Link)`
  color: #2e7d32;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Current = styled.span`
  color: #333;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const BackLink = styled(Link)`
  color: #2e7d32;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const BackChevron = styled.span`
  font-size: 1.2em;
  line-height: 1;
  color: #2e7d32;
`;

const Breadcrumb = ({ items, rightSlot }) => {
  const parent = items.length >= 2 ? items[items.length - 2] : null;
  const current = items[items.length - 1];

  return (
    <Nav aria-label="Breadcrumb">
      <List>
        {items.map((item, i) => (
          <Item key={`${item.label}-${i}`}>
            {item.href ? (
              <Crumb to={item.href}>{item.label}</Crumb>
            ) : (
              <Current aria-current="page">{item.label}</Current>
            )}
          </Item>
        ))}
      </List>
      <MobileBack>
        {parent && parent.href ? (
          <BackLink to={parent.href} aria-label={`Back to ${parent.label}`}>
            <BackChevron aria-hidden="true">‹</BackChevron>
            {parent.label}
          </BackLink>
        ) : (
          <Current aria-current="page">{current.label}</Current>
        )}
      </MobileBack>
      {rightSlot && <RightSlot>{rightSlot}</RightSlot>}
    </Nav>
  );
};

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
    })
  ).isRequired,
  rightSlot: PropTypes.node,
};

export default Breadcrumb;
